import { HargaKomoditasWidget } from "@/components/HargaKomoditasWidget"
import { SEO } from "@/components/SEO"
import { TombolBeli } from "@/components/TombolBeli"
import { Bird, Stethoscope, Syringe, Wheat, ChevronRight, ShieldAlert, ExternalLink, Beaker, Droplets, Egg, TrendingDown, Zap, ArrowRight, Lightbulb, Star } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { Link } from "react-router-dom"
import { useEffect, useState } from "react"
import { getEtalaseProducts, productImage, type EtalaseProduct } from "@/services/etalase"
import { useLibrarySection } from "@/services/content"
import { diseases as staticDiseases, type Disease } from "@/data/diseases"
import { teksDatar } from "@/types/konten"
import { chickenBreeds as staticBreeds, type ChickenBreed } from "@/data/chicken-breeds"
import { scammerList } from "@/data/scammers"
import { vaccineSchedule } from "@/data/vaccines"

interface TipsTrik {
  id: string
  title: string
  subtitle: string
  icon: React.ReactNode
  gradient: string
  borderColor: string
  steps: string[]
  relatedProducts: { name: string; shopeeUrl: string }[]
  linkTo?: string
  linkLabel?: string
}

/** Saklar bagian "Tips & Trik" di beranda. Ubah ke true untuk menampilkannya lagi. */
const TAMPILKAN_TIPS = false

const tipsTrikData: TipsTrik[] = [
  {
    id: "snot",
    title: "Penanganan SNOT (Coryza)",
    subtitle: "Ayam ngorok, mata bengkak, ingus kental",
    icon: <Droplets size={20} />,
    gradient: "from-red-500 to-rose-600",
    borderColor: "border-red-200 dark:border-red-900/50",
    steps: [
      "Isolasi ayam yang terinfeksi segera",
      "Berikan antibiotik Sulfa + Trimethoprim via air minum 3-5 hari",
      "Teteskan Gentamicin di lubang hidung 2x sehari",
      "Bersihkan ingus dengan kapas + Iodine",
      "Jaga ventilasi kandang tetap kering",
      "Vaksin Coryza untuk pencegahan"
    ],
    relatedProducts: [
      { name: "Vaksin Coryza", shopeeUrl: "https://s.shopee.co.id/9UvYEhv7Jh" },
      { name: "Iodine Antiseptik", shopeeUrl: "https://s.shopee.co.id/9Kc3Kw5I8N" }
    ],
    linkTo: "/penyakit/infectious-coryza",
    linkLabel: "Lihat detail penyakit"
  },
  {
    id: "cacingan",
    title: "Telur Cacingan / Bintik Darah",
    subtitle: "Telur ada bintik, cangkang tipis, produksi turun",
    icon: <Egg size={20} />,
    gradient: "from-amber-500 to-orange-600",
    borderColor: "border-amber-200 dark:border-amber-900/50",
    steps: [
      "Berikan obat cacing Piperazine via air minum",
      "Dosis: 1ml per 2 liter air minum selama 1 hari",
      "Ulangi pemberian 10-14 hari kemudian",
      "Buang telur selama 3 hari setelah pengobatan",
      "Bersihkan kandang & ganti litter",
      "Lakukan program pencegahan cacing rutin tiap 2-3 bulan"
    ],
    relatedProducts: [
      { name: "Hydra Clean Anti Amoniak", shopeeUrl: "https://s.shopee.co.id/4Atx9kYrHW" },
      { name: "Wipol Disinfektan", shopeeUrl: "https://s.shopee.co.id/30hzlemT71" }
    ]
  },
  {
    id: "produksi-stuck",
    title: "Produksi Telur Stuck / Turun",
    subtitle: "Hen Day turun drastis, telur kecil, cangkang lembek",
    icon: <TrendingDown size={20} />,
    gradient: "from-blue-500 to-indigo-600",
    borderColor: "border-blue-200 dark:border-blue-900/50",
    steps: [
      "Cek pencahayaan: pastikan 16-17 jam/hari",
      "Evaluasi pakan: protein minimal 17%, kalsium 3.5-4%",
      "Tambahkan suplemen vitamin + mineral",
      "Cek stres: kepadatan, suara, predator",
      "Berikan extra kalsium (tepung kerang/tulang)",
      "Pastikan air minum bersih & cukup (250ml/ekor/hari)"
    ],
    relatedProducts: [
      { name: "Hydra Plus Multivitamin", shopeeUrl: "https://s.shopee.co.id/60LbKCcsFa" },
      { name: "Hydra Bone Kalsium", shopeeUrl: "https://s.shopee.co.id/1LZllelEp7" }
    ],
    linkTo: "/racik-pakan",
    linkLabel: "Hitung racik pakan"
  },
  {
    id: "stamina-drop",
    title: "Ayam Lesu & Stamina Drop",
    subtitle: "Ayam lemas, makan sedikit, bulu kusam",
    icon: <Zap size={20} />,
    gradient: "from-emerald-500 to-teal-600",
    borderColor: "border-emerald-200 dark:border-emerald-900/50",
    steps: [
      "Berikan elektrolit + vitamin C via air minum",
      "Tambahkan suplemen energi booster",
      "Cek suhu kandang: idealnya 20-28°C",
      "Pastikan pakan segar & tidak berjamur",
      "Berikan probiotik untuk pencernaan",
      "Jika berlanjut > 3 hari, cek kemungkinan penyakit"
    ],
    relatedProducts: [
      { name: "Hydra Bro Stamina", shopeeUrl: "https://s.shopee.co.id/AABAHmgle6" },
      { name: "Hydra SSG Energi Booster", shopeeUrl: "https://s.shopee.co.id/3B1PxjeDn" }
    ],
    linkTo: "/penyakit",
    linkLabel: "Cek gejala penyakit"
  }
]

export default function HomePage() {
  const [etalaseProducts, setEtalaseProducts] = useState<EtalaseProduct[]>([])
  const [jumlahProduk, setJumlahProduk] = useState(0)
  // Isi katalog dipakai untuk mengisi kartu kategori dengan angka & contoh
  // nyata — deretan ikon tanpa keterangan tidak memberi tahu apa pun.
  const diseases = useLibrarySection<Disease>("penyakit", staticDiseases)
  const breeds = useLibrarySection<ChickenBreed>("jenis-ayam", staticBreeds)

  // Dua permintaan berbeda: beranda memajang produk FAVORIT saja, tapi angka
  // di hero harus menyebut seluruh isi etalase.
  useEffect(() => {
    getEtalaseProducts({ featuredOnly: true })
      .then((res) => setEtalaseProducts(res.items))
      .catch(() => setEtalaseProducts([]))
  }, [])

  useEffect(() => {
    getEtalaseProducts()
      .then((res) => setJumlahProduk(res.total || res.items.length))
      .catch(() => setJumlahProduk(0))
  }, [])

  const penyakitBerat = diseases.filter((d) => d.severity === "Berat")

  const kategori: KategoriInfo[] = [
    {
      title: "Penyakit",
      icon: Stethoscope,
      gradient: "from-rose-500 to-red-600",
      href: "/penyakit",
      hitung: `${diseases.length}`,
      catatan: `Gejala, obat, pencegahan · ${penyakitBerat.length} tergolong berat`,
    },
    {
      title: "Jenis Ayam",
      icon: Bird,
      gradient: "from-emerald-500 to-green-600",
      href: "/jenis-ayam",
      hitung: `${breeds.length}`,
      catatan: "Bobot, produksi telur, harga DOC",
    },
    {
      title: "Vaksinasi",
      icon: Syringe,
      gradient: "from-sky-500 to-blue-600",
      href: "/vaksinasi",
      hitung: `${vaccineSchedule.length}`,
      catatan: "Jadwal dari DOC sampai ayam dewasa",
    },
    {
      title: "Penipu",
      icon: ShieldAlert,
      gradient: "from-orange-500 to-red-600",
      href: "/penipu",
      hitung: `${scammerList.length}`,
      catatan: "Nama, rekening, dan akun yang dilaporkan",
    },
    {
      title: "Racik Pakan",
      icon: Wheat,
      gradient: "from-amber-500 to-orange-600",
      href: "/racik-pakan",
      hitung: "HPP",
      catatan: "Hitung komposisi & biaya pakan sendiri",
    },
    {
      title: "Racikan",
      icon: Beaker,
      gradient: "from-violet-500 to-purple-600",
      href: "/panduan/racikan",
      hitung: "Resep",
      catatan: "Disinfektan kandang & jamu herbal",
    },
  ]

  return (
    <div className="flex flex-col gap-6 pb-24">
      <SEO url="/" />

      {/* Hero Banner */}
      <section className="px-4">
        {/* `bg-emerald-600` bukan hiasan: kalau gradient gagal dirender (Safari
            lama), warna solid ini yang menahan agar teks putih tetap terbaca. */}
        <div className="bg-emerald-600 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 rounded-3xl p-5 text-white overflow-hidden relative">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIxLjUiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-60" />
          <Bird size={110} className="absolute -right-3 -bottom-3 text-white/15 -rotate-12" />
          <div className="relative z-10">
            <div className="max-w-[68%]">
              {/* h1 satu-satunya di beranda. Sebelumnya halaman ini mulai dari
                  h2 sehingga urutan headingnya melompat sejak baris pertama. */}
              <h1 className="text-2xl font-black mb-1">Panduan Praktis</h1>
              <p className="text-white/85 text-sm leading-snug">
                Bantu peternak temukan solusi ternak dalam hitungan detik.
              </p>
              <Link
                to="/panduan"
                className="inline-block mt-4 bg-white text-emerald-700 font-bold px-6 py-2.5 rounded-xl text-sm active:scale-95 transition-transform shadow-lg"
              >
                Mulai Belajar
              </Link>
            </div>

            {/* Isi katalog dalam angka. Angka telanjang tidak menjelaskan
                dirinya sendiri — diberi judul "Isi katalog kami" dan tiap
                angka jadi pintu masuk ke halamannya. */}
            <div className="mt-5 pt-4 border-t border-white/20">
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-2.5">
                Isi katalog kami
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { angka: diseases.length, label: "Penyakit", href: "/penyakit" },
                  { angka: breeds.length, label: "Jenis ayam", href: "/jenis-ayam" },
                  { angka: vaccineSchedule.length, label: "Jadwal vaksin", href: "/vaksinasi" },
                  { angka: jumlahProduk, label: "Produk", href: "/etalase" },
                ].map((s) => (
                  <Link
                    key={s.label}
                    to={s.href}
                    className="rounded-xl bg-white/10 hover:bg-white/20 px-3 py-2.5 transition-colors"
                  >
                    <p className="text-xl font-black leading-none tabular-nums">
                      {s.angka > 0 ? s.angka : "—"}
                    </p>
                    <p className="text-[10px] text-white/75 mt-1 leading-tight">{s.label}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Harga Komoditas Widget */}
      <section className="px-4">
        <HargaKomoditasWidget />
      </section>

      {/* Penyakit paling berbahaya — informasi nyata, bukan sekadar pintu masuk */}
      {penyakitBerat.length > 0 && (
        <section className="px-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center">
                <Stethoscope className="text-white" size={16} />
              </div>
              <h2 className="text-xl font-black">Perlu Diwaspadai</h2>
            </div>
            <Link to="/penyakit" className="text-primary font-bold text-sm active:opacity-70">
              Semua
            </Link>
          </div>
          <div className="grid gap-2">
            {penyakitBerat.slice(0, 3).map((d) => (
              <Link
                key={d.id}
                to={`/penyakit/${d.id}`}
                className="flex gap-3 items-start bg-card border rounded-2xl p-3 shadow-sm active:scale-[0.99] transition-transform"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <h3 className="font-bold text-sm leading-tight truncate">{d.name}</h3>
                    <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400">
                      {d.severity}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                    Gejala: {teksDatar(d.gejala).slice(0, 3).join(" · ") || d.description}
                  </p>
                </div>
                <ChevronRight size={16} className="text-muted-foreground shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Main Categories */}
      <section className="px-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black">Kategori Utama</h2>
          <Link to="/kategori" className="text-primary font-bold text-sm flex items-center gap-1 active:opacity-70">
            Lihat Semua <ChevronRight size={16} />
          </Link>
        </div>
        {/* Dua kolom baru di lg, bukan sm. Kartu ini butuh ±380px agar judul
            dan keterangannya muat satu baris; di 640px tiap kolom hanya dapat
            ~300px sehingga "Racik Pakan" pecah dua baris dan keterangannya
            menjadi lima baris. */}
        <div className="grid lg:grid-cols-2 gap-2.5">
          {kategori.map((k) => (
            <KategoriCard key={k.title} info={k} />
          ))}
        </div>
      </section>

      {/* Tips & Trik Peternak — disembunyikan sementara agar beranda tidak
          terlalu padat informasi. Data & komponennya sengaja dipertahankan
          supaya bisa dinyalakan lagi tanpa menulis ulang. */}
      {TAMPILKAN_TIPS && (
        <section className="px-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Lightbulb className="text-white" size={18} />
              </div>
              <h2 className="text-xl font-black">Tips & Trik Peternak</h2>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            {tipsTrikData.map((tip) => (
              <TipCard key={tip.id} tip={tip} />
            ))}
          </div>
        </section>
      )}

      {/* Produk pilihan — ditandai bintang di admin Etalase, bukan sekadar
          N produk pertama menurut urutan. */}
      {etalaseProducts.length > 0 && (
        <section className="px-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Star className="text-white fill-white" size={15} />
              </div>
              <div>
                <h2 className="text-xl font-black leading-tight">Produk Favorit</h2>
                <p className="text-[11px] text-muted-foreground">Pilihan dari etalase kami</p>
              </div>
            </div>
            <Link to="/etalase" className="text-primary font-bold text-sm active:opacity-70 shrink-0">
              Lihat Semua
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar">
            {etalaseProducts.slice(0, 10).map((product) => (
              <EtalaseCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

interface KategoriInfo {
  title: string
  icon: LucideIcon
  gradient: string
  href: string
  /** ukuran isi kategori, mis. "28" atau "Kalkulator" */
  hitung: string
  catatan: string
}

/**
 * Kartu kategori: judul, ukuran isi, satu baris keterangan.
 *
 * Versi sebelumnya juga menempelkan deretan contoh nama dalam abu-abu kecil;
 * pada kolom sempit deretan itu terpangkas di tengah kata dan justru terbaca
 * berantakan. Jumlah item sudah cukup memberi tahu kategori ini seberapa
 * berisi, tanpa membuat kartunya ramai.
 */
function KategoriCard({ info }: { info: KategoriInfo }) {
  const Icon = info.icon
  return (
    <Link
      to={info.href}
      className="flex items-center gap-3 bg-card border rounded-2xl p-3.5 shadow-sm active:scale-[0.99] transition-transform"
    >
      <div
        className={`w-10 h-10 rounded-xl bg-gradient-to-br ${info.gradient} flex items-center justify-center text-white shrink-0`}
      >
        <Icon size={19} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-sm leading-tight">{info.title}</h3>
        <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{info.catatan}</p>
      </div>
      {/* Angka dirapatkan ke kanan supaya keenam kartu punya kolom yang sejajar. */}
      <span className="shrink-0 text-[11px] font-bold text-primary tabular-nums">{info.hitung}</span>
      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
    </Link>
  )
}

function TipCard({ tip }: { tip: TipsTrik }) {
  return (
    <details className={`group bg-card border ${tip.borderColor} rounded-2xl overflow-hidden shadow-sm`}>
      <summary className="flex items-center gap-3 p-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tip.gradient} flex items-center justify-center text-white shrink-0 shadow-sm`}>
          {tip.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm leading-tight">{tip.title}</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{tip.subtitle}</p>
        </div>
        <ChevronRight size={18} className="text-muted-foreground shrink-0 transition-transform group-open:rotate-90" />
      </summary>

      <div className="px-4 pb-4 space-y-3">
        {/* Steps */}
        <div className="space-y-2">
          {tip.steps.map((step, i) => (
            <div key={i} className="flex gap-2.5 items-start">
              <span className={`w-5 h-5 rounded-full bg-gradient-to-br ${tip.gradient} text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5`}>
                {i + 1}
              </span>
              <p className="text-xs text-foreground/80 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>

        {/* Related Products */}
        {tip.relatedProducts.length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Produk Terkait</p>
            <div className="flex flex-wrap gap-2">
              {tip.relatedProducts.map((product, i) => (
                <a
                  key={i}
                  href={product.shopeeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 rounded-lg text-[11px] font-semibold active:scale-95 transition-transform border border-orange-200 dark:border-orange-900/50"
                >
                  <ExternalLink size={10} />
                  {product.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Link to detail */}
        {tip.linkTo && (
          <Link
            to={tip.linkTo}
            className={`flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r ${tip.gradient} bg-clip-text text-transparent`}
          >
            <ArrowRight size={14} className="text-muted-foreground" />
            {tip.linkLabel}
          </Link>
        )}
      </div>
    </details>
  )
}

function EtalaseCard({ product }: { product: EtalaseProduct }) {
  return (
    <div className="min-w-[160px] bg-card border rounded-2xl overflow-hidden flex flex-col shadow-sm">
      <div className="relative h-28 w-full bg-secondary">
        <img src={productImage(product) || "/placeholder.svg"} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-3">
        <h3 className="font-bold text-xs line-clamp-2 leading-tight h-8 mb-1">{product.name}</h3>

        <TombolBeli href={product.shopee_url} ukuran="sm" className="mt-3" />
      </div>
    </div>
  )
}
